import React from 'react';
import PropTypes from 'prop-types';
import { Menu, Dropdown, List, Checkbox, Divider, Button, notification, Tooltip } from 'antd';
import { useMutation } from '@apollo/client';

import ContentLibraryTree from 'pages/ContentLibrary/components/ContentLibraryTree/ContentLibraryTree';
import Loader from 'components/Loader';
import helpers from 'helpers/proposal';

import { CONTENT_LIBRARY_ADD_ITEM } from 'graphql/mutations/contentLibraryMutations';
import {
  TrashIcon,
  NewCopyIcon,
  MoveIcon,
  MenuSmallIcon,
  BackArrowIcon,
  DefaultImageIcon,
} from 'components/Icons';

import './CatalogList.scss';

/**
 * Catalog list
 *
 * @param {Object} props
 * @param {boolean} props.disabledFilter
 * @param {Function} props.onClickMenuItem
 * @param {Object} props.user
 * @param {Array} props.catalogs
 * @param {boolean} props.loadingCatalog
 * @param {Object} props.treeData
 * @param {Function} props.refetchCatalogs
 * @param {Array} props.selectedCatalogs
 */
const CatalogList = ({
  disabledFilter,
  onClickMenuItem,
  user,
  catalogs,
  loadingCatalog,
  treeData,
  refetchCatalogs,
  selectedCatalogs,
  checkedCatalogs,
  setCheckedCatalogs,
}) => {
  const [addItem] = useMutation(CONTENT_LIBRARY_ADD_ITEM, {
    onCompleted() {
      notification.success({
        description: 'Item moved successfully.',
      });
      if (refetchCatalogs) {
        refetchCatalogs();
      }
    },
    onError() {
      notification.error({
        description: 'Item moved failed.',
      });
    },
  });

  /**
   * Menu
   *
   * @param {Object} item
   */
  const menu = (item) => {
    const isAdminCatalog = item.uid === user._id || item.auid === user._id ? false : true;

    return (
      <Menu className="catalog-list__dropdown-menu">
        {!isAdminCatalog && (
          <>
            <Menu.Item
              onClick={() =>
                onClickMenuItem({ actionType: 'edit', catalogId: item._id, catalogData: item })
              }>
              Edit
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                onClickMenuItem({ actionType: 'duplicate', catalogId: item._id, catalogData: item })
              }>
              Duplicate
            </Menu.Item>

            <Menu.SubMenu
              title="Move"
              visible={true}
              disabled={treeData?.children?.length < 2}
              popupClassName="catalog-list__dropdown-menu-move">
              <div className="catalog-list__dropdown-content">
                <div className="dropdown-content__header">Move product</div>
                <Divider className="divider" />
                <ContentLibraryTree
                  onSelectTreeItem={(selectedTree) =>
                    addItem({
                      variables: {
                        type: 'catalogs',
                        fromKey: item.folderKey || '',
                        key: selectedTree.key,
                        id: selectedTree.treeId,
                        itemId: item._id,
                      },
                    })
                  }
                  onSelect={() => {}}
                  type="catalogs"
                  onUpdate={() => {}}
                  treeData={treeData || {}}
                  disableNewFolder={true}
                  fromModal={true}
                  onKeySelect={() => {}}
                />
              </div>
            </Menu.SubMenu>
            <Divider className="divider" />
            <Menu.Item
              className="delete"
              onClick={() => onClickMenuItem({ actionType: 'delete', catalogId: item._id })}>
              Delete
            </Menu.Item>
          </>
        )}

        {isAdminCatalog && (
          <>
            <Menu.Item
              onClick={() =>
                onClickMenuItem({ actionType: 'duplicate', catalogId: item._id, catalogData: item })
              }>
              Duplicate
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                onClickMenuItem({
                  actionType: 'hideorenable',
                  catalogId: item._id,
                  catalogData: item,
                })
              }>
              {disabledFilter ? 'Enable' : 'Hide'}
            </Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  /**
   * Move product menu
   *
   * @param {Object} item
   * @param {Function} dropdownMenu
   */
  const moveProductMenu = (item, dropdownMenu) => {
    return (
      <div className="dropdown-content">
        <div className="dropdown-content__header">
          <BackArrowIcon />
          Move product
        </div>
        <Divider className="divider" />
        {dropdownMenu(item)}
      </div>
    );
  };

  const renderDuration = (type, quantity) => {
    const isMatch = (label) => quantity && type === label;
    const getLabel = (label, qty) => `(for ${qty} ${label}${qty > 1 ? 's' : ''})`;

    let durationLabel;

    if (!durationLabel) {
      durationLabel = quantity && isMatch('Hourly') && getLabel('hour', quantity);
    }
    if (!durationLabel) {
      durationLabel = quantity && isMatch('Monthly') && getLabel('month', quantity);
    }
    if (!durationLabel) {
      durationLabel = quantity && isMatch('Annually') && getLabel('year', quantity);
    }

    return durationLabel || type;
  };

  const concatCurrency = (value = 0, options = {}) => {
    const {
      prop = null,
      curr = '$',
      limitDec,
      roundedTotal = true,
      parsingDecimal = false,
      y,
    } = options;

    return helpers.concatcurr({
      prop,
      x: value,
      y,
      curr,
      limitDec,
      roundedTotal,
      parsingDecimal,
    });
  };

  const isAdmin =
    selectedCatalogs &&
    catalogs.some(
      (catalog) =>
        selectedCatalogs.includes(catalog._id) &&
        (catalog.uid === user._id || catalog.auid === user._id)
    );

  return loadingCatalog ? (
    <Loader />
  ) : (
    <>
      {selectedCatalogs?.length > 0 && (
        <div className="catalog-list__selection-bar">
          <Button
            className="catalog-list__action-btn catalog-list__action-btn--copy"
            onClick={() =>
              onClickMenuItem({ actionType: 'bulk-duplicate', catalogIds: selectedCatalogs })
            }>
            <NewCopyIcon className="catalog-list__action-btn-icon" />
            Duplicate
          </Button>
          {isAdmin && (
            <>
              <Button
                className="catalog-list__action-btn catalog-list__action-btn--move"
                onClick={() =>
                  onClickMenuItem({ actionType: 'bulk-move', catalogIds: selectedCatalogs })
                }>
                <MoveIcon className="catalog-list__action-btn-icon" />
                Move
              </Button>
              <Button
                className="catalog-list__action-btn catalog-list__action-btn--delete"
                onClick={() =>
                  onClickMenuItem({ actionType: 'bulk-delete', catalogIds: selectedCatalogs })
                }>
                <TrashIcon className="catalog-list__action-btn-icon" />
                Delete
              </Button>
            </>
          )}
        </div>
      )}
      <List
        className="catalog-list"
        header={
          <div className="catalog-list__header">
            <Checkbox
              checked={checkedCatalogs}
              className="catalog-list__header-checkbox"
              onClick={() => {
                setCheckedCatalogs(!checkedCatalogs);
                onClickMenuItem({ actionType: 'select-all' });
              }}
            />
            <span>Deliverable</span>
            <span>Discount</span>
            <span>Quantity</span>
            <span className="catalog-list__header-price">Price</span>
            <span></span>
          </div>
        }
        itemLayout="horizontal"
        dataSource={catalogs}
        renderItem={(item) => (
          <List.Item className="catalog-list__row">
            <div className="catalog-list__cell catalog-list__checkbox">
              <Checkbox
                checked={selectedCatalogs.includes(item._id)}
                onClick={() => onClickMenuItem({ actionType: 'select', catalogId: item._id })}
              />
            </div>
            <div className="catalog-list__cell catalog-list__img-cell">
              <div className="catalog-list__img-container">
                {item.images?.[0]?.url ? (
                  <img src={item.images?.[0]?.url} alt={item.images?.[0]?.name || item.name} />
                ) : (
                  <div className="catalog-list__img-placeholder">
                    <DefaultImageIcon />
                  </div>
                )}
              </div>
            </div>
            <div className="catalog-list__cell catalog-list__name-cell">
              <div className="catalog-list__name">
                <Tooltip
                  title={item.name}
                  overlayStyle={{ maxWidth: '1000px' }}
                  mouseEnterDelay={0.5}>
                  <div className="catalog-list__name-text">{item.name}</div>
                </Tooltip>
              </div>
              <div className="catalog-list__desc">
                <Tooltip
                  title={item.description}
                  overlayStyle={{ maxWidth: '1000px' }}
                  mouseEnterDelay={0.5}>
                  <div className="catalog-list__desc-text">{item.description}</div>
                </Tooltip>
              </div>
            </div>
            <div className="catalog-list__cell catalog-list__discount-cell">
              {item.discount?.unit === '%'
                ? `${item.discount?.value || '0'}%`
                : item.discount?.unit
                ? concatCurrency(item.discount?.value || '0', {
                    curr: item.discount?.unit,
                    prop: user?.profile?.priceSeperator || '.',
                  })
                : concatCurrency(item.discount?.value || '0', {
                    curr: user?.profile?.currency || '$',
                    prop: user?.profile?.priceSeperator || '.',
                  })}
            </div>
            <div className="catalog-list__cell catalog-list__quantity-cell">
              {item.price?.items || '-'}
            </div>
            <div className="catalog-list__cell catalog-list__price-cell">
              <div className="catalog-list__price">
                {concatCurrency(item.price?.amount || '0', {
                  curr: user?.profile?.currency || '$',
                  prop: user?.profile?.priceSeperator || '.',
                })}
              </div>
              <div className="catalog-list__price-desc">
                {renderDuration(item.price?.type, item.price?.quantity)}
              </div>
            </div>
            <div className="catalog-list__cell catalog-list__menu-cell">
              <Dropdown
                overlay={menu(item)}
                className="menu-icon"
                trigger={['click']}
                dropdownRender={(dropdownMenu) => moveProductMenu(item, dropdownMenu)}>
                <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                  <MenuSmallIcon />
                </a>
              </Dropdown>
            </div>
          </List.Item>
        )}
        locale={{
          emptyText: (
            <div className="catalog-list__empty">
              <p>No catalogs found</p>
            </div>
          ),
        }}
      />
    </>
  );
};

/**
 * Default props
 */
CatalogList.defaultProps = {
  disabledFilter: false,
  selectedCatalogs: [],
  catalogs: [],
  loadingCatalog: false,
  treeData: {},
  refetchCatalogs: () => {},
};

/**
 * Props
 */
CatalogList.propTypes = {
  disabledFilter: PropTypes.bool,
  catalogs: PropTypes.array,
  onClickMenuItem: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(Object).isRequired,
  selectedCatalogs: PropTypes.array,
  loadingCatalog: PropTypes.bool,
  treeData: PropTypes.object,
  refetchCatalogs: PropTypes.func,
};

export default CatalogList;
